import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { PaperPlaneIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useNewsletterTemplates } from '../../../hooks/useNewsletterTemplates';
import { type Zone, extractZones } from '../../../utils/twigZones';
import type {
  Newsletter,
  NewsletterContent,
  NewsletterCreateRequest,
  NewsletterSummary,
  NewsletterUpdateRequest,
} from '../../../types/models/Newsletter';
import type { ApiResponseNewsletterSingle, ApiResponseNewsletterTest } from '../../../types/api/ApiResponseNewsletter';
import type { ApiResponseNewsletterTemplateSingle } from '../../../types/api/ApiResponseNewsletterTemplate';
import NewsletterZoneEditor from './NewsletterZoneEditor';

const SUBJECT_MAX = 255;

// Field-error keys rendered inline on their input; everything else (content, html, newsletter, body,
// template, id) lands in the form-level alert.
const FIELD_ERROR_KEYS = new Set(['subject', 'newsletter_template_id']);

interface NewsletterEditorModalProps {
  /** Summary row to edit (must be a draft), or null to create a new newsletter. */
  newsletter: NewsletterSummary | null;
  onClose: () => void;
  /** Hand the created/updated full newsletter back so the parent can upsert its row. */
  onSaved: (saved: Newsletter) => void;
}

/**
 * Create/edit modal for a newsletter. Composes a subject, a template picker (the newsletter is built
 * on a `newsletter_template`), and a zone editor whose fields are derived by parsing the selected
 * template's Twig ({@link extractZones}) — so staff fill the template's editable zones and the
 * backend renders `content` through the template server-side. Only DRAFT newsletters are editable
 * (the list only offers Modify on drafts; the backend enforces it too).
 *
 * `html` is never sent from here: the render pipeline (preview/test/send) re-renders from `content`,
 * and the send worker writes the final HTML snapshot itself. On EDIT the full newsletter is fetched
 * once per open (a ref guard stops an OIDC token-renew from re-running the fetch and clobbering
 * unsaved edits — see [[useapifetch-identity-flips-on-token-renew]]).
 */
function NewsletterEditorModal({ newsletter, onClose, onSaved }: NewsletterEditorModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const user = useUserContext();
  const { templates, loading: templatesLoading } = useNewsletterTemplates(false);

  const isEdit = newsletter !== null;

  const [subject, setSubject] = useState(newsletter?.subject ?? '');
  const [templateId, setTemplateId] = useState<number | null>(newsletter?.newsletter_template_id ?? null);
  const [content, setContent] = useState<NewsletterContent>({});
  const [zones, setZones] = useState<Zone[]>([]);

  // Change-set baselines (an edit starts from the fetched newsletter; a create from empty).
  const [originalSubject, setOriginalSubject] = useState(newsletter?.subject ?? '');
  const [originalTemplateId, setOriginalTemplateId] = useState<number | null>(
    newsletter?.newsletter_template_id ?? null,
  );
  const [originalContent, setOriginalContent] = useState('{}');

  const [loadingNewsletter, setLoadingNewsletter] = useState(isEdit);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);
  const [loadingZones, setLoadingZones] = useState(false);
  const [zonesError, setZonesError] = useState<string | null>(null);
  const loadedTwigForId = useRef<number | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // "Send a Preview" runs a save-then-test in one shot while keeping the modal open; its own flag drives
  // that button's spinner and locks the rest of the form (see `isBusy`) without spinning the Save button.
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Raw-JSON escape hatch: the zone parser is best-effort (a macro-call arg or a `{% set x = y %}`
  // RHS may not get its own field), so staff can edit the whole `content` object directly. `content`
  // stays the source of truth; JSON edits write to it on every valid parse.
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // --- Edit: fetch the full newsletter (content) exactly once per open. ---
  const hasFetchedRef = useRef(false);
  const loadNewsletter = useCallback(async () => {
    if (newsletter === null) {
      return;
    }
    setLoadingNewsletter(true);
    setNewsletterError(null);
    try {
      const response = await apiFetch(`/admin/newsletters/${newsletter.id}`);
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterSingle | null;
      if (body?.status === 'success' && body.data?.newsletter) {
        const full = body.data.newsletter;
        const nextContent = full.content ?? {};
        setSubject(full.subject);
        setTemplateId(full.newsletter_template_id);
        setContent(nextContent);
        setOriginalSubject(full.subject);
        setOriginalTemplateId(full.newsletter_template_id);
        setOriginalContent(JSON.stringify(nextContent));
      } else {
        setNewsletterError(body?.message ?? 'Could not load the newsletter. Try reopening the editor.');
      }
    } catch (err) {
      setNewsletterError(err instanceof Error ? err.message : 'Could not load the newsletter.');
    } finally {
      setLoadingNewsletter(false);
    }
  }, [apiFetch, newsletter]);

  useEffect(() => {
    if (isEdit && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void loadNewsletter();
    }
  }, [isEdit, loadNewsletter]);

  // --- Load the selected template's Twig and derive its zones. Keyed on templateId, so an OIDC
  //     token-renew (which changes apiFetch's identity) does NOT refetch the same template. ---
  const loadTwig = useCallback(
    async (id: number) => {
      setLoadingZones(true);
      setZonesError(null);
      try {
        const response = await apiFetch(`/admin/newsletters/templates/${id}`);
        const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTemplateSingle | null;
        if (body?.status === 'success' && body.data?.newsletter_template) {
          setZones(extractZones(body.data.newsletter_template.twig));
          loadedTwigForId.current = id;
        } else {
          setZonesError(body?.message ?? 'Could not load the template.');
        }
      } catch (err) {
        setZonesError(err instanceof Error ? err.message : 'Could not load the template.');
      } finally {
        setLoadingZones(false);
      }
    },
    [apiFetch],
  );

  useEffect(() => {
    // Wait for the edit fetch to seed templateId; skip if this template's zones are already loaded.
    if (loadingNewsletter || templateId === null || loadedTwigForId.current === templateId) {
      return;
    }
    void loadTwig(templateId);
  }, [templateId, loadingNewsletter, loadTwig]);

  const toggleJsonMode = (next: boolean) => {
    if (next) {
      setJsonText(JSON.stringify(content, null, 2));
      setJsonError(null);
    }
    setJsonMode(next);
  };

  const onJsonChange = (value: string) => {
    setJsonText(value);
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError('Content must be a JSON object, e.g. { "title": "…" }.');
        return;
      }
      setContent(parsed as NewsletterContent);
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? `Invalid JSON: ${err.message}` : 'Invalid JSON.');
    }
  };

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const trimmedSubject = subject.trim();
  const subjectChanged = trimmedSubject !== originalSubject.trim();
  const templateChanged = templateId !== originalTemplateId;
  const contentChanged = JSON.stringify(content) !== originalContent;
  const hasChanges = subjectChanged || templateChanged || contentChanged;

  // Either action (Save, or the save-then-test behind "Send a Preview") locks the form while in flight.
  const isBusy = isSaving || isPreviewing;

  // Shared validity floor for both footer actions: not busy, the entity loaded, and the fields are sane.
  const isFormValid =
    !isBusy &&
    !loadingNewsletter &&
    newsletterError === null &&
    trimmedSubject !== '' &&
    trimmedSubject.length <= SUBJECT_MAX &&
    templateId !== null &&
    jsonError === null;

  const canSubmit = isFormValid && (isEdit ? hasChanges : true);

  // Preview is edit-only (it needs a persisted id to render/test) and — unlike Save — does NOT require
  // pending changes: an already-saved draft can still be proofed.
  const canPreview = isEdit && isFormValid;

  const contentToSend = (): NewsletterContent | null => (Object.keys(content).length > 0 ? content : null);

  const buildCreateBody = (): NewsletterCreateRequest => ({
    newsletter_template_id: templateId as number,
    subject: trimmedSubject,
    content: contentToSend(),
  });

  const buildUpdateBody = (): NewsletterUpdateRequest => {
    const patch: NewsletterUpdateRequest = {};
    if (subjectChanged) {
      patch.subject = trimmedSubject;
    }
    if (templateChanged && templateId !== null) {
      patch.newsletter_template_id = templateId;
    }
    if (contentChanged) {
      patch.content = contentToSend();
    }
    return patch;
  };

  const splitFieldErrors = (
    body: ApiResponseNewsletterSingle | null,
  ): { errors: Record<string, string>; general: string | null } => {
    const errors: Record<string, string> = {};
    const generalParts: string[] = [];
    if (body?.data) {
      for (const [key, value] of Object.entries(body.data)) {
        if (typeof value !== 'string') {
          continue; // skips the newsletter object on the success shape
        }
        if (FIELD_ERROR_KEYS.has(key)) {
          errors[key] = value;
        } else {
          generalParts.push(value);
        }
      }
    }
    let general = generalParts.length > 0 ? generalParts.join(' ') : null;
    if (general === null && Object.keys(errors).length === 0) {
      general = body?.message ?? 'Could not save the newsletter. Please try again.';
    }
    return { errors, general };
  };

  /**
   * Send the create/update request. On success it re-baselines the change-set to what was just saved
   * (so `hasChanges` resets) and hands the row back to the parent, then returns the saved newsletter —
   * it deliberately does NOT notify success or close, so each caller decides what happens next (Save
   * closes; "Send a Preview" persists then tests, staying open). On failure it surfaces the field/form
   * errors + a danger notification and returns null. Callers own `isSaving`/`isPreviewing` and clear
   * the errors before calling.
   */
  const persist = async (): Promise<Newsletter | null> => {
    try {
      const response = await apiFetch(isEdit ? `/admin/newsletters/${newsletter.id}` : '/admin/newsletters', {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(isEdit ? buildUpdateBody() : buildCreateBody()),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterSingle | null;

      if (body?.status === 'success' && body.data?.newsletter) {
        const saved = body.data.newsletter;
        onSaved(saved);
        // Re-baseline to the editor state we just persisted so `hasChanges` is false until the next edit.
        setOriginalSubject(trimmedSubject);
        setOriginalTemplateId(templateId);
        setOriginalContent(JSON.stringify(content));
        return saved;
      }

      const { errors, general } = splitFieldErrors(body);
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update newsletter' : 'Could not create newsletter',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return null;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update newsletter' : 'Could not create newsletter',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      return null;
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const saved = await persist();
      if (saved === null) {
        return;
      }
      addNotification({
        title: isEdit ? 'Newsletter updated' : 'Newsletter created',
        srTitle: 'Success alert',
        description: `${saved.subject} was ${isEdit ? 'updated' : 'created'} as a draft.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * "Send a Preview": persist any pending edits (skipping the PATCH when nothing changed — an empty
   * change-set is rejected by the backend), then send ONE proof of the saved draft to the logged-in
   * staffer's own address via `POST …/test` (renders the real newsletter; leaves it a draft). The
   * modal stays open so they can keep editing after checking how it looks.
   */
  const handlePreview = async () => {
    if (newsletter === null) {
      return;
    }
    setIsPreviewing(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const savedEdits = hasChanges;
      if (savedEdits && (await persist()) === null) {
        return; // save failed — persist() already surfaced the error, so skip the test send.
      }

      const email = (user?.email ?? '').trim();
      if (email === '') {
        const message = 'Your account has no email address on file, so a preview cannot be sent.';
        setFormError(message);
        addNotification({
          title: 'Could not send preview',
          srTitle: 'Error alert',
          description: message,
          variant: 'danger',
        });
        setDrawerOpen(true);
        return;
      }

      const response = await apiFetch(`/admin/newsletters/${newsletter.id}/test`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      // Branch on the JSend `body.status`, NEVER `response.ok`.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTest | null;

      if (body?.status === 'success' && body.data?.sent) {
        const to = typeof body.data.to === 'string' ? body.data.to : email;
        addNotification({
          title: 'Preview sent',
          srTitle: 'Success alert',
          description: savedEdits
            ? `Your changes were saved and a preview of ${trimmedSubject} was sent to ${to}.`
            : `A preview of ${trimmedSubject} was sent to ${to}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return;
      }

      // Field errors: `email` (invalid), `template` (render failure).
      const fieldMessage =
        typeof body?.data?.email === 'string'
          ? (body.data.email as string)
          : typeof body?.data?.template === 'string'
            ? (body.data.template as string)
            : null;
      const message = fieldMessage ?? body?.message ?? 'Could not send the preview. Please try again.';
      setFormError(message);
      addNotification({
        title: 'Could not send preview',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: 'Could not send preview',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsPreviewing(false);
    }
  };

  const subjectError = fieldErrors['subject'];
  const templateError = fieldErrors['newsletter_template_id'];
  const noTemplates = !templatesLoading && templates.length === 0;

  const renderZonesSection = () => {
    if (templateId === null) {
      return (
        <FormHelperText>
          <HelperText>
            <HelperTextItem>Select a template to edit its content.</HelperTextItem>
          </HelperText>
        </FormHelperText>
      );
    }
    if (loadingZones) {
      return (
        <Bullseye className="pf-v6-u-py-lg">
          <Spinner size="lg" aria-label="Loading template zones" />
        </Bullseye>
      );
    }
    if (zonesError !== null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Could not load the template"
          actionLinks={
            <Button variant="link" isInline onClick={() => void loadTwig(templateId)}>
              Retry
            </Button>
          }
        >
          {zonesError}
        </Alert>
      );
    }
    return (
      <>
        <div className="pf-v6-u-mb-sm">
          <Switch
            id="newsletter-content-json-toggle"
            label="Edit raw content (JSON)"
            isChecked={jsonMode}
            onChange={(_event, checked) => toggleJsonMode(checked)}
            // While the JSON is invalid, block switching back to the form (it would silently drop the
            // broken edits) — the user must fix or clear the JSON first.
            isDisabled={isBusy || (jsonMode && jsonError !== null)}
          />
        </div>
        {jsonMode ? (
          <FormGroup label="Content (JSON)" fieldId="newsletter-content-json">
            <TextArea
              id="newsletter-content-json"
              aria-label="Newsletter content as JSON"
              value={jsonText}
              onChange={(_event, value) => onJsonChange(value)}
              validated={jsonError !== null ? 'error' : 'default'}
              isDisabled={isBusy}
              resizeOrientation="vertical"
              rows={14}
              style={{ fontFamily: 'var(--pf-t--global--font--family--mono, monospace)' }}
            />
            <FormHelperText>
              <HelperText>
                {jsonError !== null ? (
                  <HelperTextItem variant="error">{jsonError}</HelperTextItem>
                ) : (
                  <HelperTextItem>
                    The full editable-zone object sent to the backend — use this to fill zones the form didn’t detect.
                  </HelperTextItem>
                )}
              </HelperText>
            </FormHelperText>
          </FormGroup>
        ) : zones.length === 0 ? (
          <Alert variant="info" isInline title="No editable zones detected" className="pf-v6-u-mt-sm">
            The form found no editable zones in this template. If it should carry content, switch on “Edit raw content
            (JSON)” above to enter it directly.
          </Alert>
        ) : (
          <NewsletterZoneEditor zones={zones} content={content} onChange={setContent} isDisabled={isBusy} />
        )}
      </>
    );
  };

  return (
    <Modal
      variant="large"
      isOpen
      onClose={onClose}
      aria-label={isEdit ? 'Edit newsletter' : 'Create newsletter'}
    >
      <ModalHeader title={isEdit ? 'Edit newsletter' : 'Create newsletter'} />
      <ModalBody>
        {loadingNewsletter ? (
          <Bullseye className="pf-v6-u-py-xl">
            <Spinner size="xl" aria-label="Loading newsletter" />
          </Bullseye>
        ) : newsletterError !== null ? (
          <Alert
            variant="danger"
            isInline
            title="Could not load the newsletter"
            actionLinks={
              <Button variant="link" isInline onClick={() => void loadNewsletter()}>
                Retry
              </Button>
            }
          >
            {newsletterError}
          </Alert>
        ) : (
          <Form
            id="newsletter-editor-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit) {
                void handleSubmit();
              }
            }}
          >
            {formError !== null && (
              <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Creation failed'} className="pf-v6-u-mb-md">
                {formError}
              </Alert>
            )}
            {noTemplates && (
              <Alert variant="warning" isInline title="No templates available" className="pf-v6-u-mb-md">
                Create a newsletter template first — a newsletter is built on a template.
              </Alert>
            )}

            <FormGroup label="Subject" isRequired fieldId="newsletter-subject">
              <TextInput
                id="newsletter-subject"
                type="text"
                isRequired
                value={subject}
                onChange={(_event, value) => {
                  setSubject(value);
                  clearFieldError('subject');
                }}
                validated={subjectError !== undefined || trimmedSubject.length > SUBJECT_MAX ? 'error' : 'default'}
                isDisabled={isBusy}
              />
              <FormHelperText>
                <HelperText>
                  {subjectError !== undefined ? (
                    <HelperTextItem variant="error">{subjectError}</HelperTextItem>
                  ) : trimmedSubject.length > SUBJECT_MAX ? (
                    <HelperTextItem variant="error">Subject must be {SUBJECT_MAX} characters or fewer.</HelperTextItem>
                  ) : (
                    <HelperTextItem>The email subject line (1–{SUBJECT_MAX} characters).</HelperTextItem>
                  )}
                </HelperText>
              </FormHelperText>
            </FormGroup>

            <FormGroup label="Template" isRequired fieldId="newsletter-template">
              <FormSelect
                id="newsletter-template"
                value={templateId ?? ''}
                onChange={(_event, value) => {
                  setTemplateId(value === '' ? null : Number(value));
                  clearFieldError('newsletter_template_id');
                }}
                aria-label="Newsletter template"
                validated={templateError !== undefined ? 'error' : 'default'}
                isDisabled={isBusy || templatesLoading || noTemplates}
              >
                <FormSelectOption value="" label={templatesLoading ? 'Loading templates…' : 'Select a template'} isDisabled />
                {templates.map((tpl) => (
                  <FormSelectOption key={tpl.id} value={tpl.id} label={tpl.name} />
                ))}
              </FormSelect>
              {templateError !== undefined && (
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem variant="error">{templateError}</HelperTextItem>
                  </HelperText>
                </FormHelperText>
              )}
            </FormGroup>

            {renderZonesSection()}
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="newsletter-editor-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        {isEdit && (
          <Button
            variant="secondary"
            type="button"
            icon={<PaperPlaneIcon />}
            onClick={() => void handlePreview()}
            isDisabled={!canPreview}
            isLoading={isPreviewing}
          >
            Send a Preview
          </Button>
        )}
        <Button variant="link" onClick={onClose} isDisabled={isBusy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NewsletterEditorModal;
